import React, { FC, useCallback, useMemo, useState } from "react";
import { isSameDay, subDays } from "date-fns";
import MuiButton from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import styles from "./DashboardDateFilter.module.scss";

/** Date range configuration */
const DATE_RANGE_CONFIG = {
    /** Earliest date for data (May 1, 2024) */
    MIN_DATE: new Date(2024, 4, 1), // Month is 0-indexed, so 4 = May
    /** Default date range in days */
    DEFAULT_DAYS_BACK: 30
} as const;

export type QuickDateSelectType = 'last7' | 'last30' | 'previousMonth' | 'previousQuarter' | 'monthToDate' | 'quarterToDate' | 'yearToDate' | 'allTime';

export interface DashboardDateFilterProps {
    /**
     * Current start date
     */
    startDate: Date;
    /**
     * Current end date
     */
    endDate: Date;
    /**
     * Callback when date range changes
     */
    onDateRangeChange: (startDate: Date, endDate: Date) => void;
    /**
     * Optional: Which quick select options to show in the dropdown
     * Defaults to ['last7', 'last30', 'previousMonth', 'previousQuarter']
     */
    quickSelectOptions?: QuickDateSelectType[];
    /**
     * Optional: Custom class name for the container
     */
    className?: string;
}

const QUICK_SELECT_LABELS: Record<QuickDateSelectType, string> = {
    last7: 'Last 7 Days',
    last30: 'Last 30 Days',
    previousMonth: 'Previous Month',
    previousQuarter: 'Previous Quarter',
    monthToDate: 'Month to Date',
    quarterToDate: 'Quarter to Date',
    yearToDate: 'Year to Date',
    allTime: 'All Time'
};

/**
 * Reusable date filter component for dashboards.
 * Provides "Month to Date" button, a quick select dropdown, and date inputs.
 */
export const DashboardDateFilter: FC<DashboardDateFilterProps> = ({
    startDate,
    endDate,
    onDateRangeChange,
    quickSelectOptions = ['last7', 'last30', 'previousMonth', 'previousQuarter', 'quarterToDate', 'yearToDate', 'allTime'],
    className
}) => {
    const now = useMemo(() => new Date(), []);

    // Quick select menu state
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    }, []);

    const handleMenuClose = useCallback(() => {
        setMenuAnchor(null);
    }, []);

    // Date range constraints
    const minDate = DATE_RANGE_CONFIG.MIN_DATE;
    const maxDate = now;

    // Convert date to YYYY-MM-DD format for input[type="date"]
    const formatDateForInput = (date: Date): string => {
        return date.toISOString().split('T')[0];
    };

    // Detect which preset matches the current date range
    const detectCurrentPreset = useMemo((): QuickDateSelectType | 'custom' => {
        const today = new Date();

        // Check Month to Date
        const monthToDateStart = new Date(today.getFullYear(), today.getMonth(), 1);
        if (isSameDay(startDate, monthToDateStart) && isSameDay(endDate, today)) {
            return 'monthToDate';
        }

        // Check Quarter to Date
        const currentQuarter = Math.floor(today.getMonth() / 3);
        const quarterToDateStart = new Date(today.getFullYear(), currentQuarter * 3, 1);
        if (isSameDay(startDate, quarterToDateStart) && isSameDay(endDate, today)) {
            return 'quarterToDate';
        }

        // Check Year to Date
        const yearToDateStart = new Date(today.getFullYear(), 0, 1);
        if (isSameDay(startDate, yearToDateStart) && isSameDay(endDate, today)) {
            return 'yearToDate';
        }

        // Check Last 7 Days
        const last7Start = new Date();
        last7Start.setDate(last7Start.getDate() - 7);
        if (isSameDay(startDate, last7Start) && isSameDay(endDate, today)) {
            return 'last7';
        }

        // Check Last 30 Days
        const last30Start = new Date();
        last30Start.setDate(last30Start.getDate() - 30);
        if (isSameDay(startDate, last30Start) && isSameDay(endDate, today)) {
            return 'last30';
        }

        // Check Previous Month
        const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        if (isSameDay(startDate, prevMonthStart) && isSameDay(endDate, prevMonthEnd)) {
            return 'previousMonth';
        }

        // Check Previous Quarter
        const previousQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
        const prevQuarterYear = currentQuarter === 0 ? today.getFullYear() - 1 : today.getFullYear();
        const prevQuarterStart = new Date(prevQuarterYear, previousQuarter * 3, 1);
        const prevQuarterEnd = new Date(prevQuarterYear, previousQuarter * 3 + 3, 0);
        if (isSameDay(startDate, prevQuarterStart) && isSameDay(endDate, prevQuarterEnd)) {
            return 'previousQuarter';
        }

        // Check All Time (start date matches configured minimum date)
        if (isSameDay(startDate, DATE_RANGE_CONFIG.MIN_DATE) && isSameDay(endDate, today)) {
            return 'allTime';
        }

        return 'custom';
    }, [startDate, endDate]);

    // Get the label for the dropdown button
    const dropdownButtonLabel = useMemo(() => {
        if (detectCurrentPreset === 'custom') {
            return 'Custom';
        }
        return QUICK_SELECT_LABELS[detectCurrentPreset];
    }, [detectCurrentPreset]);

    const handleQuickDateSelect = useCallback((type: QuickDateSelectType) => {
        const today = new Date();
        let newStart: Date;
        let newEnd: Date = today;

        switch (type) {
            case 'last7':
                newStart = new Date();
                newStart.setDate(newStart.getDate() - 7);
                break;
            case 'last30':
                newStart = new Date();
                newStart.setDate(newStart.getDate() - 30);
                break;
            case 'previousMonth':
                newStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                newEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case 'previousQuarter': {
                const currentQuarter = Math.floor(today.getMonth() / 3);
                const previousQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
                const year = currentQuarter === 0 ? today.getFullYear() - 1 : today.getFullYear();
                newStart = new Date(year, previousQuarter * 3, 1);
                newEnd = new Date(year, previousQuarter * 3 + 3, 0);
                break;
            }
            case 'monthToDate':
                newStart = new Date(today.getFullYear(), today.getMonth(), 1);
                break;
            case 'quarterToDate': {
                const currentQuarter = Math.floor(today.getMonth() / 3);
                newStart = new Date(today.getFullYear(), currentQuarter * 3, 1);
                break;
            }
            case 'yearToDate':
                newStart = new Date(today.getFullYear(), 0, 1);
                break;
            case 'allTime':
                // Use the configured minimum date
                newStart = DATE_RANGE_CONFIG.MIN_DATE;
                break;
        }

        handleMenuClose();
        onDateRangeChange(newStart, newEnd);
    }, [onDateRangeChange, handleMenuClose]);

    const handleStartDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value) {
            const newStart = new Date(e.target.value);
            // Validate that start date is not after end date
            if (newStart > endDate) {
                console.warn('[DashboardDateFilter] Start date cannot be after end date');
                return;
            }
            onDateRangeChange(newStart, endDate);
        }
    }, [endDate, onDateRangeChange]);

    const handleEndDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value) {
            const newEnd = new Date(e.target.value);
            // Validate that end date is not before start date
            if (newEnd < startDate) {
                console.warn('[DashboardDateFilter] End date cannot be before start date');
                return;
            }
            onDateRangeChange(startDate, newEnd);
        }
    }, [startDate, onDateRangeChange]);

    return (
        <div className={`${styles.dateFilterSection} ${className || ''}`}>
            {/* Month to Date - primary action */}
            <MuiButton
                variant="outlined"
                size="small"
                onClick={() => handleQuickDateSelect('monthToDate')}
                sx={{
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    height: '39px',
                    textTransform: 'none',
                    color: '#333',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        borderColor: '#999'
                    }
                }}
            >
                {QUICK_SELECT_LABELS.monthToDate}
            </MuiButton>

            {/* Quick Select dropdown */}
            <MuiButton
                onClick={handleMenuOpen}
                size="small"
                variant="outlined"
                startIcon={<CalendarTodayIcon sx={{ fontSize: 18 }} />}
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                sx={{
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    height: '39px',
                    minWidth: '180px',
                    textTransform: 'none',
                    color: '#333',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& .MuiButton-startIcon': {
                        marginRight: '6px',
                        display: 'flex',
                        alignItems: 'center'
                    },
                    '& .MuiButton-endIcon': {
                        marginLeft: '6px',
                        display: 'flex',
                        alignItems: 'center'
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        borderColor: '#999'
                    }
                }}
                aria-label="Quick select date range"
            >
                {dropdownButtonLabel}
            </MuiButton>
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <ListSubheader>Quick Select</ListSubheader>
                {quickSelectOptions.map(option => (
                    <MenuItem
                        key={option}
                        onClick={() => handleQuickDateSelect(option)}
                    >
                        {QUICK_SELECT_LABELS[option]}
                    </MenuItem>
                ))}
            </Menu>

            {/* Date inputs */}
            <div className={styles.dateInputs}>
                <input
                    type="date"
                    value={formatDateForInput(startDate)}
                    min={formatDateForInput(minDate)}
                    max={formatDateForInput(maxDate)}
                    onChange={handleStartDateChange}
                />
                <span className={styles.dateSeparator}>to</span>
                <input
                    type="date"
                    value={formatDateForInput(endDate)}
                    min={formatDateForInput(minDate)}
                    max={formatDateForInput(maxDate)}
                    onChange={handleEndDateChange}
                />
            </div>
        </div>
    );
};

/**
 * Hook to manage dashboard date filter state with URL persistence
 */
export const useDashboardDateFilter = (defaultDaysBack: number = DATE_RANGE_CONFIG.DEFAULT_DAYS_BACK) => {
    // Memoize the "now" reference so it doesn't change on every render
    const now = useMemo(() => new Date(), []);

    // Memoize default dates so they're stable references
    const defaultStartDate = useMemo(() => {
        return subDays(now, defaultDaysBack);
    }, [now, defaultDaysBack]);

    const defaultEndDate = now;

    const parseDateFromString = useCallback((dateString: string | null, defaultDate: Date): Date => {
        if (dateString) {
            const parsed = new Date(dateString);
            if (!isNaN(parsed.getTime())) return parsed;
        }
        return defaultDate;
    }, []);

    return {
        defaultStartDate,
        defaultEndDate,
        parseDateFromString
    };
};
